import React from "react";
import { TableCell, TableRow } from "@mui/material";
import styles from "../dealerDashBoard/DashboardStyles.module.scss";
import { Link } from "react-router-dom";
import MotionChevronRight from "../../../shared/Icons/MotionChevronRight";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { IS_CANADA_MARKET } from "../../../utils/EmpUtil";
import UiDateUtils from "../../../utils/UiDateUtils";
import {
  ACCESSORIES_PRICING,
  ALD_PRICING,
  CUPID_ID,
  CUSTOMER_CONTRACT,
  DELIVERY_BANDS,
  GPIS_PRICING,
  LEAD_TIMES,
  NEW_VEHICLE_LINE,
  OPT_IN,
  PAYMENT_THRESHOLD,
  PRICING,
  RESERVATION_DEPOSIT,
  STORE_ID,
  TERMS_AND_CONDITIONS,
  VARIABLE_MARKETING,
} from "../../../common/Constants";

const BUSINESS_SETTINGS_PATH = "/business-settings";
const TERMS_AND_CONDITIONS_TEST_ACTION = "TERMS_AND_CONDITIONS";

const BUSINESS_SETTINGS_ACTIONS = new Set([
  TERMS_AND_CONDITIONS,
  TERMS_AND_CONDITIONS_TEST_ACTION,
  STORE_ID,
  PAYMENT_THRESHOLD,
  LEAD_TIMES,
  CUPID_ID,
  DELIVERY_BANDS,
  VARIABLE_MARKETING,
  OPT_IN,
  CUSTOMER_CONTRACT,
  ALD_PRICING,
  GPIS_PRICING,
]);

const VEHICLE_PRICING_ACTIONS = new Set([
  "VEHICLE_PRICE_CHANGE",
  NEW_VEHICLE_LINE,
  PRICING,
]);

const BUSINESS_TABS = new Map([
  [DELIVERY_BANDS, 2],
  [STORE_ID, 3],
  [CUSTOMER_CONTRACT, 4],
  [VARIABLE_MARKETING, 5],
  [OPT_IN, 5],
  [ALD_PRICING, 5],
  [GPIS_PRICING, 5],
  [TERMS_AND_CONDITIONS, -1],
  [TERMS_AND_CONDITIONS_TEST_ACTION, -1],
]);

const getBusinessTabs = (actionName) => BUSINESS_TABS.get(actionName);

const getColor = (status) => {
  switch (status) {
    case "AMBER":
      return styles.amber;
    case "RED":
    case "OVERDUE":
      return styles.red;
    case "GREEN":
    default:
      return "";
  }
};

const getActionPathname = (actionName) => {
  if (BUSINESS_SETTINGS_ACTIONS.has(actionName)) {
    return BUSINESS_SETTINGS_PATH;
  }

  if (VEHICLE_PRICING_ACTIONS.has(actionName)) {
    return "/pricing/vehicles";
  }

  if (actionName === RESERVATION_DEPOSIT) {
    return "/pricing/deposit";
  }

  if (actionName === ACCESSORIES_PRICING) {
    return "/pricing/accessories";
  }

  return "";
};

const getActionToPerform = (actionName, t, translationKey) => {
  const pathname = getActionPathname(actionName);

  return {
    pathname,
    navigateText: pathname ? t(`${translationKey}.go`) : "",
  };
};

const getDueDate = (pendingAction, props, translationKey) => {
  if (pendingAction.status === "OVERDUE") {
    return props.t(`${translationKey}.overdue`);
  }

  if (IS_CANADA_MARKET(props.user.market)) {
    return UiDateUtils.formatDateDDMMYYYYToMMDDYYYY(
      pendingAction.dueDate,
      "/",
      "/"
    );
  }

  return pendingAction.dueDate;
};

const updateBusinessSettings = (actionName, props) => {
  if (!props.isNsc) {
    props.user.updateBusinessSettingsTab?.(getBusinessTabs(actionName));
  }
};

const renderActionLink = (pendingAction, actions, props, vehicleLine) => {
  const hasNavigationAction = Boolean(actions.pathname);

  if (!hasNavigationAction) {
    return null;
  }

  return (
    <Link
      onClick={() => updateBusinessSettings(pendingAction.actionName, props)}
      data-testid={`${pendingAction.actionName}_GO`}
      to={{
        pathname: actions.pathname,
        query: {
          fromPendingActions: true,
          modelYear: vehicleLine?.year,
          model: vehicleLine?.model,
        },
        actionName: pendingAction.actionName,
      }}
      className={styles.goToPage}
    >
      {actions.navigateText}
      <MotionChevronRight />
    </Link>
  );
};

const renderPendingActionsRow = (pendingAction, index, props, vehicleLine) => {
  const translationKey = props.isNsc
    ? "NscPendingActions"
    : "DealerPendingActions";

  const actionName = props.t(`${translationKey}.${pendingAction.actionName}`);

  const vehicleLineDescription = vehicleLine
    ? ` for ${vehicleLine.year} ${vehicleLine.model}`
    : "";

  const actionDescription =
    props.t(`${translationKey}Description.${pendingAction.actionName}`) +
    vehicleLineDescription;

  const actions = getActionToPerform(
    pendingAction.actionName,
    props.t,
    translationKey
  );

  const rowColor = getColor(pendingAction.status);
  const dueDate = getDueDate(pendingAction, props, translationKey);

  return (
    <React.Fragment key={index}>
      <TableRow
        className={`${styles.textContentPendingActions} ${rowColor}`}
        data-testid={pendingAction.actionName}
      >
        <TableCell>
          <div className={styles.alignStart}>
            <ReportProblemOutlinedIcon className={styles.actionIcon} />
            <span className={styles.actionName}>{actionName}</span>
          </div>
        </TableCell>

        <TableCell className={styles.col2}>
          <div className={styles.alignStart}>
            <span className={styles.description}>{actionDescription}</span>
          </div>
        </TableCell>

        <TableCell className={styles.col3}>
          <div className={styles.alignStart}>{dueDate}</div>
        </TableCell>

        <TableCell>
          <div>
            {renderActionLink(pendingAction, actions, props, vehicleLine)}
          </div>
        </TableCell>

        <TableCell />
      </TableRow>
    </React.Fragment>
  );
};

export const PendingActionsRow = (pendingAction, index, props, vehicleLine) => {
  return renderPendingActionsRow(pendingAction, index, props, vehicleLine);
};
